import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../App";
import functions from "../../functions/functions";
import NumericPinPad from "../ui/NumericPinPad";
import AlertIcon from "../../icons/AlertIcon";
import { useAppStore } from "../../store/useAppStore";

export default function PaymentPopup() {
	const {
		shop,
		setPaymentPopup,
		total,
		config,
		cart,
		updateOrderFromCart,
		checkout,
		setCart,
		setInvoicePopup,
		apiSettings,
		fiscalPrinter,
		setEreciptPopup,
		calculateRoundForTotal,
		isErecipt,
	} = useContext(AppContext);

	const { totalBeforeRound, round, payed, setPayed } = useAppStore();

	const [rest, setRest] = useState(0);
	const [remaining, setRemaining] = useState(0);
	const [paidDisplay, setPaidDisplay] = useState(0);
	const [selectedAmount, setSelectedAmount] = useState(0);
	const [resetPinpad, setResetPinpad] = useState(false);
	const [error, setError] = useState(false);
	const [errorLock, setErrorLock] = useState(false);

	const toCents = (v) => Math.round(Number(v) * 100);
	const fromCents = (c) => Number((c / 100).toFixed(2));
	const formatMoney = (v) => {
		const n = Number(v);
		return functions.formatter.format(Number.isFinite(n) ? n : 0);
	};

	const creditTotal = Number(cart.credit_total) || 0;
	const customerCreditBalance = Number(cart.customer?.credit) || 0;
	const grossTotal = total + creditTotal;

	/* =========================
	   CALCOLO PAGATO / RESTO
	   ========================= */
	useEffect(() => {
		let paidCents = 0;
		let paidDisplayCents = 0;
		let cashGivenCents = 0;
		let cashTakenCents = 0;

		for (const p of cart.payments) {
			paidCents += toCents(p.amount);
			paidDisplayCents += toCents(
				p.payment_method_id === 1 ? p.o_amount : p.amount
			);

			if (p.payment_method_id === 1) {
				cashGivenCents += toCents(p.o_amount);
				cashTakenCents += toCents(p.amount);
			}
		}

		const totalCents = toCents(total);

		setPayed(fromCents(paidCents));
		setPaidDisplay(fromCents(paidDisplayCents + toCents(creditTotal)));
		setRemaining(fromCents(Math.max(0, totalCents - paidCents)));
		setRest(fromCents(Math.max(0, cashGivenCents - cashTakenCents)));
	}, [cart, total, creditTotal, setPayed]);

	/* =========================
	   CONTROLLI ERRORI
	   ========================= */
	useEffect(() => {
		const electronic = cart.payments.find((p) => p.payment_method_id == 14);
		if (electronic && toCents(payed) > toCents(total)) {
			setError("L'importo del pagamento elettronico non deve superare il totale");
			setErrorLock(true);
		} else {
			setErrorLock(false);
		}
	}, [payed, cart, total]);

	/* =========================
	   ADD PAYMENT
	   ========================= */
	const addPayment = (payment) => {
		const hasManual = selectedAmount && selectedAmount > 0;
		const manualCents = hasManual ? toCents(functions.roundNumber(selectedAmount)) : 0;

		const base = Number(totalBeforeRound);
		const finalTotal =
			payment.payment_method_id == 1
				? base + calculateRoundForTotal(base, true)
				: total;

		const totalCents = toCents(finalTotal);
		const paidCents = cart.payments.reduce((s, p) => s + toCents(p.amount), 0);
		const remainingCents = Math.max(0, totalCents - paidCents);

		/* --- blocco altri metodi se già coperto --- */
		if (payment.payment_method_id != 1 && remainingCents == 0) {
			setError("Non puoi aggiungere altri pagamenti se il totale è già coperto");
			return;
		}

		/* --- Training esclusivo --- */
		if (payment.payment_method_id == 16 && cart.payments.length > 0) {
			setError("Il pagamento Training non può essere combinato con altri metodi");
			return;
		}
		if (payment.payment_method_id != 16 && is_training()) {
			setError("Non puoi aggiungere altri metodi se è già presente Training");
			return;
		}

		/* --- Pagamento elettronico non duplicabile --- */
		if (payment.payment_method_id == 14 && cart.payments.some((p) => p.payment_method_id == 14)) {
			setError("Il pagamento elettronico è già stato aggiunto");
			return;
		}

		/* =========================
		   CONTANTI
		   ========================= */
		if (payment.payment_method_id == 1) {
			if (hasManual) {
				payment.o_amount = fromCents(manualCents);
				payment.amount = fromCents(Math.min(manualCents, remainingCents));
			} else {
				payment.o_amount = fromCents(remainingCents);
				payment.amount = fromCents(remainingCents);
			}
		} else {
			/* =========================
		   NON CONTANTI
		   ========================= */
			let payCents = hasManual ? manualCents : remainingCents;
			payCents = Math.min(payCents, remainingCents);

			if (payment.payment_method_id == 15 && payCents !== totalCents) {
				setError("L'importo del pagamento non riscosso deve essere uguale al totale");
				return;
			}

			payment.o_amount = fromCents(payCents);
			payment.amount = fromCents(payCents);
		}

		setCart((prev) => ({
			...prev,
			payments: [...prev.payments, payment],
		}));

		setResetPinpad((v) => !v);
		setError(false);
	};

	/* =========================
	   DELETE PAYMENT
	   ========================= */
	const deletePayment = (i) => {
		setCart((prev) => ({
			...prev,
			payments: prev.payments.filter((_, idx) => idx !== i),
		}));
		setError(false);
	};

	const addCredit = () => {
		const hasManual = selectedAmount && selectedAmount > 0;
		const manualCents = hasManual ? toCents(functions.roundNumber(selectedAmount)) : 0;

		const paidCents = cart.payments.reduce((s, p) => s + toCents(p.amount), 0);
		const remainingCents = Math.max(0, toCents(total) - paidCents);

		let creditCents = hasManual ? manualCents : remainingCents;
		creditCents = Math.min(creditCents, remainingCents);
		creditCents = Math.min(creditCents, toCents(customerCreditBalance));

		if (creditCents <= 0) return;

		setCart((prev) => ({ ...prev, credit_total: fromCents(creditCents) }));
		setResetPinpad((v) => !v);
		setError(false);
	};

	const removeCredit = () => {
		setCart((prev) => ({ ...prev, credit_total: 0 }));
	};

	const isEditing = () => !!cart.order_id;

	const is_training = () => cart.payments.some((p) => p.payment_method_id == 16);

	/* =========================
	   RENDER
	   ========================= */
	const hasPaymentMethods = Array.isArray(shop?.payment_methods) && shop.payment_methods.length > 0;
	const trainingPayment = is_training();
	const mainButtonsClassName =
		"payment-popup-main-buttons" +
		(trainingPayment ? " payment-popup-main-buttons-full" : "");
	const mainPaymentButtonLabel = trainingPayment
		? "Chiudi ordine"
		: apiSettings.mandant.fiscal_printers == 1
		? "Scontrino"
		: "Completa";

	return (
		<div className="payment-back">
			<div className="payment">
				<div className="payment-header">
					<div className="title">Seleziona il metodo di pagamento</div>
					<button onClick={() => setPaymentPopup(false)}>&times;</button>
				</div>

				<div className="payment-body">
					<div className="cols">
						<div className="left">
							<NumericPinPad
								onSubmit={(val) => setSelectedAmount(val)}
								autoSubmit
								onReset={resetPinpad}
							/>
						</div>

						<div className="right">
							<div className="right-header">
								{round !== 0 && (
									<>
										<div className="subtotal">
											<div>Subtotale:</div>
											<div>{formatMoney(totalBeforeRound)}</div>
										</div>
										<div className="subtotal">
											<div>Arrotondamento:</div>
											<div>{formatMoney(round)}</div>
										</div>
									</>
								)}
								<div>
									<div className="title">Importo totale</div>
									<div className="total">{formatMoney(grossTotal)}</div>
								</div>
							</div>

							<div className="right-content">
								{creditTotal > 0 && (
									<div className="payment-selected-row payment-selected-credit">
										<div className="payment-selected-name">Credito cliente</div>
										<div className="payment-selected-meta">
											<div className="payment-selected-amount">
												- {formatMoney(creditTotal)}
											</div>
											<button onClick={removeCredit}>&times;</button>
										</div>
									</div>
								)}
								{cart.payments.map((p, i) => (
									<div key={i} className="payment-selected-row">
										<div className="payment-selected-name">
											{functions.getPaymentMethodName(p.payment_method_id)}
											{p.gateway === "voucherly" && " (Voucherly)"}
										</div>
										<div className="payment-selected-meta">
											<div className="payment-selected-amount">
											{formatMoney(p.payment_method_id == 1 ? p.o_amount : p.amount)}
											</div>
											{/* quota già incassata dal gateway: non si toglie dal POS */}
											{!p.gateway && <button onClick={() => deletePayment(i)}>&times;</button>}
										</div>
									</div>
								))}
								{error && (
									<div className="error">
										<AlertIcon /> {error}
									</div>
								)}
							</div>

							<div className="right-footer">
								<div className="total-payed">
									<div>
										<div className="title">Da Pagare</div>
										<div>{formatMoney(total)}</div>
									</div>
									<div>
										<div className="title">Pagato</div>
										<div>{formatMoney(paidDisplay)}</div>
									</div>
									{remaining > 0 ? (
										<div>
											<div className="title">Mancano</div>
											<div>{formatMoney(remaining)}</div>
										</div>
									) : (
										<div>
											<div className="title">Resto</div>
											<div>{formatMoney(rest)}</div>
										</div>
									)}
								</div>

								{remaining == 0 && !errorLock && (
									<div className={mainButtonsClassName}>
										{!trainingPayment && (
											<button
												onClick={() => {
													setPaymentPopup(false);
													setInvoicePopup(true);
												}}
											>
												{apiSettings.mandant.invoices == 1
													? "Fattura"
													: "Fattura Manuale"}
											</button>
										)}

										{!isErecipt() ? (
											<button
												onClick={() => {
													setPaymentPopup(false);
													isEditing()
														? updateOrderFromCart(
																false,
																true,
																config.prebill_on_checkout == 1
														  )
														: checkout(
																false,
																false,
																true,
																false,
																config.prebill_on_checkout == 1
														  );
												}}
											>
												{mainPaymentButtonLabel}
											</button>
										) : (
											<button
												onClick={() => {
													setPaymentPopup(false);
													setEreciptPopup(true);
												}}
											>
												Scontrino
											</button>
										)}
									</div>
								)}
							</div>
						</div>
					</div>

				<div className="payment-methods-section">
					{hasPaymentMethods ? (
						<div className="list mode-4">
							{shop.payment_methods.map((c, i) => (
								<a key={i} className="list-element" onClick={() => addPayment({ payment_method_id: c.id })}>
									<div className="icon">
										<img src={"/payments/" + c.id + ".png"} alt={c.name} />
									</div>
									{c.name}
								</a>
							))}
							{apiSettings.credit?.active == 1 &&
							 cart.customer &&
							 customerCreditBalance > 0 && (
								<a className="list-element payment-credit-element" onClick={addCredit}>
									<div className="payment-credit-label">Credito cliente</div>
									<div className="payment-credit-balance">Saldo {formatMoney(customerCreditBalance)}</div>
								</a>
							)}
						</div>
					) : (
						<div className="payment-methods-empty">
							Nessun metodo di pagamento attivo - Attivali dalle impostazioni del negozio nel
							backoffice
						</div>
					)}
				</div>
			</div>
		</div>
		</div>
	);
}
